import {useCallback, type ComponentType} from "react";
import {motion} from "motion/react";
import {Globe, Star, Users, Newspaper} from "lucide-react";
import {SiX, SiFacebook, SiYoutube, SiInstagram} from "@icons-pack/react-simple-icons";
import {Tooltip, TooltipTrigger, TooltipContent} from "@/components/ui/tooltip";
import {cn} from "@/lib/utils";
import {useIsMobile} from "@/hooks/use-mobile";
import {useSidebar} from "@/components/ui/sidebar";

// Platform icon mapping
const PLATFORM_ICONS: Record<string, ComponentType<{className?: string}>> = {
  web: Globe,
  twitter_x: SiX,
  facebook: SiFacebook,
  youtube: SiYoutube,
  instagram: SiInstagram,
  reviews: Star,
  other_social_media: Users,
  gdelt: Newspaper,
};

interface Platform {
  id: string;
  name: string;
  background_color: string;
}

interface PlatformDockProps {
  platforms: Platform[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  disabled?: boolean;
  className?: string;
}

interface DockItemProps {
  platform: Platform;
  isActive: boolean;
  onClick: () => void;
}

/**
 * Determine if a hex color is light (for icon contrast)
 */
function isLightColor(hex: string): boolean {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return false;

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

/**
 * Convert hex color to rgba string
 */
function hexToRgba(hex: string, alpha: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(0,0,0,${alpha})`;

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Individual dock item
 */
function DockItem({platform, isActive, onClick}: DockItemProps) {
  const Icon = PLATFORM_ICONS[platform.id] || Globe;

  // Determine if this is a light or dark background for icon contrast
  const isLightBg = isLightColor(platform.background_color);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.button
          type="button"
          onClick={onClick}
          className={cn(
            "relative flex shrink-0 items-center justify-center rounded-xl",
            "focus-visible:ring-foreground/50 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
            "transition-opacity duration-200",
            isActive ? "opacity-100" : "opacity-50 hover:opacity-70"
          )}
          style={{
            width: 48,
            height: 48,
            backgroundColor: platform.background_color,
            boxShadow: isActive
              ? `0 0 20px ${hexToRgba(platform.background_color, 0.5)}, 0 4px 12px ${hexToRgba(platform.background_color, 0.3)}`
              : "0 2px 8px rgba(0,0,0,0.1)",
          }}
          whileHover={{scale: 1.1}}
          whileTap={{scale: 0.95}}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 25,
          }}
          aria-label={`${isActive ? "Deselect" : "Select"} ${platform.name}`}
          aria-pressed={isActive}
        >
          {/* Subtle inner highlight for depth */}
          <div
            className={cn(
              "absolute inset-0 rounded-xl",
              "from-foreground/20 bg-gradient-to-b to-transparent",
              "pointer-events-none"
            )}
          />

          {/* Icon */}
          <Icon
            className={cn(
              "relative z-10 h-6 w-6 transition-colors",
              isLightBg ? "text-gray-800" : "text-foreground"
            )}
          />

          {/* macOS-style active indicator dot - positioned absolutely below button */}
          <motion.div
            className="bg-foreground absolute -bottom-2.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full"
            initial={false}
            animate={{
              scale: isActive ? 1 : 0,
            }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30,
            }}
          />
        </motion.button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={12}>
        {platform.name}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Dock-style platform filter.
 * Fixed at bottom of screen on desktop (offset for sidebar), horizontally scrollable on mobile.
 */
export function PlatformDock({
  platforms,
  selectedIds,
  onSelectionChange,
  disabled = false,
  className,
}: PlatformDockProps) {
  const isMobile = useIsMobile();
  const {state: sidebarState} = useSidebar();
  const isSidebarCollapsed = sidebarState === "collapsed";

  const handleToggle = useCallback(
    (platformId: string) => {
      if (disabled) return;

      // If all platforms are selected (empty array), deselecting one means selecting all EXCEPT that one
      if (selectedIds.length === 0) {
        const allExceptClicked = platforms.filter((p) => p.id !== platformId).map((p) => p.id);
        onSelectionChange(allExceptClicked);
        return;
      }

      const isCurrentlySelected = selectedIds.includes(platformId);
      const newSelection = isCurrentlySelected
        ? selectedIds.filter((id) => id !== platformId)
        : [...selectedIds, platformId];

      // If all platforms are now selected, reset to empty array (meaning "all")
      if (newSelection.length === platforms.length) {
        onSelectionChange([]);
      } else {
        onSelectionChange(newSelection);
      }
    },
    [selectedIds, platforms, onSelectionChange, disabled]
  );

  if (platforms.length === 0) {
    return (
      <div className="text-muted-foreground py-4 text-center text-sm">No platforms available</div>
    );
  }

  return (
    <div
      className={cn(
        // Base styles - fixed at bottom on both mobile and desktop
        "fixed bottom-4 z-50",
        "flex items-center gap-2 px-4 pt-3 pb-4",
        // Glassmorphism
        "bg-gray-900/60 backdrop-blur-xl",
        "border-foreground/10 border",
        "rounded-2xl",
        "shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)]",
        // Mobile: full width with horizontal scroll
        isMobile && "scrollbar-hide right-4 left-4 overflow-x-auto",
        // Desktop: centered with dynamic sidebar offset
        // Expanded sidebar: 16rem / 2 = 8rem, Collapsed sidebar: 3rem / 2 = 1.5rem
        !isMobile && "-translate-x-1/2 transition-[left] duration-200",
        !isMobile && isSidebarCollapsed && "left-[calc(50%+1.5rem)]",
        !isMobile && !isSidebarCollapsed && "left-[calc(50%+8rem)]",
        // Disabled state
        disabled && "pointer-events-none opacity-50",
        className
      )}
    >
      {/* Subtle gradient overlay for depth */}
      <div
        className={cn(
          "pointer-events-none absolute inset-0 rounded-2xl",
          "from-background/10 to-foreground/5 bg-gradient-to-t via-transparent"
        )}
      />

      {platforms.map((platform) => (
        <DockItem
          key={platform.id}
          platform={platform}
          isActive={selectedIds.length === 0 || selectedIds.includes(platform.id)}
          onClick={() => handleToggle(platform.id)}
        />
      ))}
    </div>
  );
}
